import { useState, type ReactNode } from 'react';
import Card, { CardBody } from './Card';

interface CollapsibleSectionProps {
  /** Überschrift im Header. Bei einem String wird automatisch ein `<h3>` mit
   *  Standard-Styling gerendert, sodass das Layout zu nicht-klappbaren
   *  Sektionen passt. */
  title: ReactNode;
  /** Optional: kompakte Vorschau, die im _eingeklappten_ Zustand im Body
   *  angezeigt wird. Wenn nicht gesetzt und eingeklappt: nur Header sichtbar. */
  summary?: ReactNode;
  /** Inhalt, der nur im ausgeklappten Zustand gerendert wird. */
  children: ReactNode;
  /** Default-Zustand. Default: `false` (eingeklappt). */
  defaultOpen?: boolean;
  /** Zusätzliche Klassen für den äußeren Container. */
  className?: string;
}

/**
 * Wiederverwendbare einklappbare Sektion. Header-Optik ist identisch zu
 * nicht-klappbaren Sektionen (`CardHeader`)
 * — der einzige sichtbare Zusatz ist ein Toggle-Pfeil rechts. Damit sehen
 * klappbare und nicht-klappbare Cards im Stack einheitlich aus.
 *
 * Der gesamte Header ist ein `<button>` mit `aria-expanded` für Screenreader.
 */
export default function CollapsibleSection({
  title,
  summary,
  children,
  defaultOpen = false,
  className,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const hasSummary = summary !== undefined && summary !== null && summary !== false;
  const showBody = open || hasSummary;

  return (
    <Card className={className}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`card-header w-full flex items-center justify-between gap-3 text-left hover:bg-gray-50 transition-colors rounded-t-lg ${showBody ? '' : 'border-b-0'}`}
        aria-expanded={open}
      >
        <div className="flex-1 min-w-0">
          {typeof title === 'string' ? (
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          ) : (
            title
          )}
        </div>
        <span
          className={`text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        >
          ▼
        </span>
      </button>
      {showBody && (
        <CardBody>
          {open ? children : summary}
        </CardBody>
      )}
    </Card>
  );
}
